import type { OrgStatus } from '../types/constants';

/**
 * The plans, and what each one unlocks.
 *
 * One table, read by the client to decide what to show and by the functions
 * to decide what to allow. A plan is a field on the organisation set by the
 * platform owner; nothing in the product changes it. A workspace still on
 * its trial gets the Team plan's features so the trial is a trial of
 * something; after that, whatever was set, or Starter.
 *
 * Prices are per seat per month. They are derived from what a seat costs to
 * run on Firebase at a ten-to-one markup; the working is in docs/pricing.md
 * and the figures here are the ones the landing page prints, so the two
 * cannot drift apart.
 */
export const PLANS = ['free', 'team', 'business', 'enterprise'] as const;
export type Plan = (typeof PLANS)[number];

export const PLAN_FEATURES = [
  // ----- Team -----
  'capacity',
  'budgets',
  'savedViews',
  'history',
  'reportsAll',
  'csvExport',
  'branding',
  'emailNotifications',
  'calendar',
  'watchers',
  'recurrence',
  'relationships',
  'milestones',
  'projectHealth',
  'taskImport',
  'taskExport',
  'dueReminders',
  'weeklyDigest',
  'teamHub',
  'lineup',
  'calibration',
  'leaveAwareWork',
  'runway',
  'goals',
  'automations',
  'burndown',
  'timeline',
  'dependencies',
  'brief',
  'intakeForms',
  'honestDates',
  // ----- Business -----
  'scheduledReports',
  'auditLog',
  'fullExport',
  'projectTemplates',
  'bulkAllowances',
  'activityFeed',
  'holidays',
  'finance',
  'retainers',
  'clientLinks',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

export interface PlanLimits {
  /** Active members allowed. Null is unlimited. */
  seats: number | null;
  projects: number | null;
  clients: number | null;
  features: readonly PlanFeature[];
}

const TEAM_FEATURES: readonly PlanFeature[] = [
  'capacity',
  'budgets',
  'savedViews',
  'history',
  'reportsAll',
  'csvExport',
  'branding',
  'emailNotifications',
  'calendar',
  'watchers',
  'recurrence',
  'relationships',
  'milestones',
  'projectHealth',
  'taskImport',
  'taskExport',
  'dueReminders',
  'weeklyDigest',
  'teamHub',
  'lineup',
  'calibration',
  'leaveAwareWork',
  'runway',
  'goals',
  'automations',
  'burndown',
  'timeline',
  'dependencies',
  'brief',
  'intakeForms',
  'honestDates',
];

const BUSINESS_FEATURES: readonly PlanFeature[] = [
  ...TEAM_FEATURES,
  'scheduledReports',
  'auditLog',
  'fullExport',
  'projectTemplates',
  'bulkAllowances',
  'activityFeed',
  'holidays',
  'finance',
  'retainers',
  'clientLinks',
];

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: { seats: 3, projects: 3, clients: 2, features: [] },
  team: { seats: 25, projects: null, clients: null, features: TEAM_FEATURES },
  business: { seats: 100, projects: null, clients: null, features: BUSINESS_FEATURES },
  enterprise: { seats: null, projects: null, clients: null, features: BUSINESS_FEATURES },
};

export const PLAN_LABELS: Record<Plan, string> = {
  free: 'Starter',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
};

/**
 * Per seat per month, in US dollars. Monthly is the card price; yearly is
 * what a seat costs per month when paid for the year. Zero is free; null is
 * "talk to us".
 */
export const PLAN_PRICES: Record<Plan, { monthly: number | null; yearly: number | null }> = {
  free: { monthly: 0, yearly: 0 },
  team: { monthly: 2, yearly: 1.6 },
  business: { monthly: 4, yearly: 3.2 },
  enterprise: { monthly: null, yearly: null },
};

/** Enterprise is priced by conversation; this is the floor the conversation starts from. */
export const ENTERPRISE_MIN_SEATS = 50;
export const ENTERPRISE_FROM_PER_SEAT = 7;

export function isPlan(value: unknown): value is Plan {
  return typeof value === 'string' && (PLANS as readonly string[]).includes(value);
}

/** The plan a workspace is actually on: what was set, or Team while on trial, or Free. */
export function effectivePlan(org: { plan?: Plan | null; status: OrgStatus }): Plan {
  if (org.plan && isPlan(org.plan)) return org.plan;
  return org.status === 'trial' ? 'team' : 'free';
}

export function planAllows(plan: Plan, feature: PlanFeature): boolean {
  return PLAN_LIMITS[plan].features.includes(feature);
}

export function seatLimit(plan: Plan): number | null {
  return PLAN_LIMITS[plan].seats;
}

/** The lowest plan that includes a feature, for "available on Team" hints. */
export function planFor(feature: PlanFeature): Plan {
  for (const plan of PLANS) if (planAllows(plan, feature)) return plan;
  return 'enterprise';
}
